<template>
  <div
    class="min-h-full bg-slate-50/60 dark:bg-[#0B1120] text-slate-900 dark:text-[#F1F5F9] pb-16 transition-colors"
  >
    <div class="w-full lg:max-w-[1140px] xl:max-w-[1260px] mx-auto px-2 sm:px-4 md:px-6 py-3 sm:py-4 md:py-6 space-y-4 sm:space-y-6">
      <!-- Onboarding / Status Banner -->
      <Banner />

      <!-- Top Header Card -->
      <header
        class="bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-md rounded-2xl p-3 sm:p-5 border border-slate-200/90 dark:border-[#263449] shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 transition-all"
      >
        <!-- Identity & Status -->
        <div class="flex items-center gap-2.5 sm:gap-3 min-w-0 flex-1">
          <div
            class="w-9 h-9 sm:w-11 sm:h-11 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-900/40 flex items-center justify-center shrink-0 shadow-xs"
          >
            <UserCheck class="w-4.5 h-4.5 sm:w-5 sm:h-5" />
          </div>
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-1.5 sm:gap-2 flex-wrap">
              <h1
                class="text-base sm:text-xl font-black text-slate-900 dark:text-[#F1F5F9] tracking-tight truncate"
              >
                Teacher Attendance
              </h1>
              <span
                class="inline-flex items-center gap-1.5 px-2 sm:px-2.5 py-0.5 rounded-full text-[10px] sm:text-[11px] font-bold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/40 shadow-xs shrink-0"
              >
                <span
                  class="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"
                ></span>
                Live Roster
              </span>
            </div>
            <p
              class="text-xs text-slate-500 dark:text-[#A6B4C8] font-medium mt-0.5 truncate sm:whitespace-normal"
            >
              Monitor faculty check-in logs, arrival timestamps, and daily presence
            </p>
          </div>
        </div>

        <!-- Controls: Current Range Pill, Notifications & Refresh -->
        <div
          class="flex items-center justify-between sm:justify-end gap-2 w-full sm:w-auto pt-2.5 sm:pt-0 border-t sm:border-t-0 border-slate-100 dark:border-[#263449]/70 shrink-0"
        >
          <!-- Active Range Display Pill -->
          <div
            class="flex items-center gap-1.5 bg-slate-50 dark:bg-[#18243A] border border-slate-200/80 dark:border-[#263449] px-2.5 sm:px-3 py-1.5 rounded-xl text-[11px] sm:text-xs font-semibold text-slate-700 dark:text-[#F1F5F9] shadow-2xs min-w-0 max-w-[190px] xs:max-w-xs sm:max-w-none"
          >
            <Calendar class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 shrink-0" />
            <span class="truncate">{{ attendanceRangeLabel }}</span>
          </div>

          <div class="flex items-center gap-2 shrink-0">
            <!-- Notifications Bell & Dropdown -->
            <div class="relative">
              <button
                ref="notificationButtonRef"
                @click="showNotifications = !showNotifications"
                type="button"
                class="relative p-2 rounded-xl bg-slate-50 dark:bg-[#18243A] hover:bg-slate-100 dark:hover:bg-[#172235] text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9] border border-slate-200/80 dark:border-[#263449] transition focus:outline-none shadow-xs"
                title="View attendance alerts"
              >
                <Bell class="h-4.5 w-4.5" />
                <span
                  v-if="notifications.length > 0"
                  class="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-black rounded-full h-4.5 min-w-[18px] px-1 flex items-center justify-center shadow-xs"
                >
                  {{ notifications.length > 99 ? "99+" : notifications.length }}
                </span>
              </button>

              <!-- Notifications Dropdown Panel -->
              <transition
                enter-active-class="transition duration-200 ease-out"
                enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-150 ease-in"
                leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0"
              >
                <div
                  v-if="showNotifications"
                  ref="notificationDropdownRef"
                  class="absolute right-0 mt-2 w-[calc(100vw-2.5rem)] max-w-sm sm:w-96 bg-white dark:bg-[#111C30] rounded-2xl shadow-2xl border border-slate-200 dark:border-[#263449] z-50 overflow-hidden backdrop-blur-md"
                >
                <!-- Header -->
                <div
                  class="p-4 border-b border-slate-100 dark:border-[#263449] bg-slate-50/70 dark:bg-[#0F172A] flex items-center justify-between"
                >
                  <div class="flex items-center gap-2">
                    <Bell class="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <h3
                      class="text-sm font-bold text-slate-900 dark:text-[#F1F5F9]"
                    >
                      Notifications
                    </h3>
                  </div>
                  <span
                    class="text-xs px-2 py-0.5 rounded-full font-semibold bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border border-blue-200/50 dark:border-blue-800/30"
                  >
                    {{ notifications.length }} new
                  </span>
                </div>

                <!-- Notifications List -->
                <div class="max-h-80 overflow-y-auto divide-y divide-slate-100 dark:divide-[#18243A]">
                  <div
                    v-if="isLoadingNotifications"
                    class="p-6 text-center"
                  >
                    <AppLoader
                      variant="inline"
                      size="sm"
                      message="Updating notifications..."
                      layout="vertical"
                      color="indigo"
                    />
                  </div>

                  <div
                    v-else-if="notifications.length === 0"
                    class="p-8 text-center"
                  >
                    <div
                      class="w-12 h-12 rounded-full bg-slate-100 dark:bg-[#18243A] flex items-center justify-center mx-auto mb-3"
                    >
                      <Bell class="h-6 w-6 text-slate-400 dark:text-[#7E8DA5]" />
                    </div>
                    <p
                      class="text-sm font-semibold text-slate-800 dark:text-[#F1F5F9]"
                    >
                      No new alerts
                    </p>
                    <p class="text-xs text-slate-400 dark:text-[#7E8DA5] mt-0.5">
                      Everything is up to date
                    </p>
                  </div>

                  <div
                    v-else
                    v-for="notification in notifications"
                    :key="notification.id || notification"
                    class="p-3.5 hover:bg-slate-50 dark:hover:bg-[#18243A]/60 transition-colors"
                  >
                    <div class="flex items-start gap-3">
                      <div
                        :class="[
                          'flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center shadow-xs',
                          getNotificationIconClass(notification),
                        ]"
                      >
                        <component
                          :is="getNotificationIcon(notification)"
                          class="w-4 h-4"
                        />
                      </div>
                      <div class="flex-1 min-w-0">
                        <div class="flex items-center justify-between gap-1 mb-0.5">
                          <p
                            class="text-xs font-bold text-slate-800 dark:text-[#F1F5F9] truncate"
                          >
                            {{ getNotificationTitle(notification) }}
                          </p>
                          <span
                            :class="[
                              'text-[10px] font-bold px-1.5 py-0.2 rounded-full capitalize',
                              getNotificationSeverityClass(notification),
                            ]"
                          >
                            {{ getNotificationSeverity(notification) }}
                          </span>
                        </div>
                        <p
                          class="text-xs text-slate-600 dark:text-[#A6B4C8] line-clamp-2"
                        >
                          {{ getNotificationMessage(notification) }}
                        </p>
                        <p
                          class="text-[10px] text-slate-400 dark:text-[#7E8DA5] mt-1 flex items-center gap-1 font-medium"
                        >
                          <Clock class="w-3 h-3" />
                          {{ formatNotificationTime(notification) }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Footer -->
                <div
                  class="p-2.5 border-t border-slate-100 dark:border-[#263449] bg-slate-50/70 dark:bg-[#0F172A]"
                >
                  <button
                    @click="refreshNotifications"
                    :disabled="isLoadingNotifications"
                    class="w-full py-2 px-3 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/40 rounded-xl font-bold transition flex items-center justify-center gap-1.5 disabled:opacity-50"
                  >
                    <RefreshCw
                      class="w-3.5 h-3.5"
                      :class="{ 'animate-spin': isLoadingNotifications }"
                    />
                    <span>Refresh Alerts</span>
                  </button>
                </div>
              </div>
            </transition>
          </div>

          <!-- Refresh Button -->
          <button
            @click="fetchAttendanceData(currentPage)"
            :disabled="isTableLoading"
            class="flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-xs transition disabled:opacity-50 shrink-0"
            title="Refresh attendance records"
          >
            <RefreshCw
              class="w-3.5 h-3.5"
              :class="{ 'animate-spin': isTableLoading }"
            />
            <span class="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>
    </header>

      <!-- Key Metrics & Statistics Cards (Matching Student design system) -->
      <section class="space-y-2.5">
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3.5 lg:gap-4">
          <!-- Total Staff -->
          <div
            class="bg-white dark:bg-[#111C30] border border-slate-200/90 dark:border-[#263449] rounded-2xl p-3.5 sm:p-4 shadow-xs hover:shadow-md hover:border-slate-300 dark:hover:border-slate-700 transition-all duration-300 flex flex-col justify-between overflow-hidden relative group"
          >
            <div>
              <div class="flex items-center justify-between mb-2">
                <div
                  class="w-8 h-8 sm:w-9 sm:h-9 bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200/60 dark:border-indigo-800/40 rounded-xl text-indigo-600 dark:text-indigo-400 flex items-center justify-center shadow-xs group-hover:scale-105 transition-transform"
                >
                  <Users class="w-4 h-4 sm:w-4.5 sm:h-4.5" />
                </div>
                <span
                  class="text-[10px] font-bold bg-slate-100 dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] px-2 py-0.5 rounded-md border border-slate-200/60 dark:border-[#263449]"
                >
                  Active Roster
                </span>
              </div>
              <h3 class="text-xs font-bold text-slate-600 dark:text-[#A6B4C8]">
                Total Staff
              </h3>
              <p class="text-2xl sm:text-3xl font-black text-slate-900 dark:text-[#F1F5F9] mt-0.5 tracking-tight">
                {{ summaryStats.total }}
              </p>
            </div>
            <div class="mt-2.5">
              <div class="w-full bg-slate-100 dark:bg-[#18243A] h-2 rounded-full overflow-hidden">
                <div
                  class="bg-indigo-500 h-full rounded-full transition-all duration-500"
                  style="width: 100%"
                ></div>
              </div>
              <p class="text-slate-500 dark:text-[#7E8DA5] text-[11px] font-medium mt-1.5">
                Faculty enrolled in school
              </p>
            </div>
          </div>

          <!-- Present Faculty -->
          <div
            class="bg-white dark:bg-[#111C30] border border-slate-200/90 dark:border-[#263449] rounded-2xl p-3.5 sm:p-4 shadow-xs hover:shadow-md hover:border-slate-300 dark:hover:border-slate-700 transition-all duration-300 flex flex-col justify-between overflow-hidden relative group"
          >
            <div>
              <div class="flex items-center justify-between mb-2">
                <div
                  class="w-8 h-8 sm:w-9 sm:h-9 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200/60 dark:border-emerald-800/40 rounded-xl text-emerald-600 dark:text-emerald-400 flex items-center justify-center shadow-xs group-hover:scale-105 transition-transform"
                >
                  <UserCheck class="w-4 h-4 sm:w-4.5 sm:h-4.5" />
                </div>
                <span
                  class="text-[10px] font-bold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 px-2 py-0.5 rounded-md border border-emerald-200/60 dark:border-emerald-800/40"
                >
                  {{ attendanceRate }}% Present
                </span>
              </div>
              <h3 class="text-xs font-bold text-slate-600 dark:text-[#A6B4C8]">
                Present Today
              </h3>
              <p class="text-2xl sm:text-3xl font-black text-slate-900 dark:text-[#F1F5F9] mt-0.5 tracking-tight">
                {{ summaryStats.present }}
              </p>
            </div>
            <div class="mt-2.5">
              <div class="w-full bg-slate-100 dark:bg-[#18243A] h-2 rounded-full overflow-hidden">
                <div
                  class="bg-emerald-500 h-full rounded-full transition-all duration-500"
                  :style="{ width: `${attendanceRate}%` }"
                ></div>
              </div>
              <p class="text-slate-500 dark:text-[#7E8DA5] text-[11px] font-medium mt-1.5">
                Checked-in faculty members
              </p>
            </div>
          </div>

          <!-- Late Arrivals -->
          <div
            class="bg-white dark:bg-[#111C30] border border-slate-200/90 dark:border-[#263449] rounded-2xl p-3.5 sm:p-4 shadow-xs hover:shadow-md hover:border-slate-300 dark:hover:border-slate-700 transition-all duration-300 flex flex-col justify-between overflow-hidden relative group"
          >
            <div>
              <div class="flex items-center justify-between mb-2">
                <div
                  class="w-8 h-8 sm:w-9 sm:h-9 bg-amber-50 dark:bg-amber-950/40 border border-amber-200/60 dark:border-amber-800/40 rounded-xl text-amber-600 dark:text-amber-400 flex items-center justify-center shadow-xs group-hover:scale-105 transition-transform"
                >
                  <Clock class="w-4 h-4 sm:w-4.5 sm:h-4.5" />
                </div>
                <span
                  class="text-[10px] font-bold bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 px-2 py-0.5 rounded-md border border-amber-200/60 dark:border-amber-800/40"
                >
                  Delayed
                </span>
              </div>
              <h3 class="text-xs font-bold text-slate-600 dark:text-[#A6B4C8]">
                Late Arrivals
              </h3>
              <p class="text-2xl sm:text-3xl font-black text-slate-900 dark:text-[#F1F5F9] mt-0.5 tracking-tight">
                {{ summaryStats.late }}
              </p>
            </div>
            <div class="mt-2.5">
              <div class="w-full bg-slate-100 dark:bg-[#18243A] h-2 rounded-full overflow-hidden">
                <div
                  class="bg-amber-500 h-full rounded-full transition-all duration-500"
                  :style="{
                    width: `${summaryStats.total > 0 ? (summaryStats.late / summaryStats.total) * 100 : 0}%`,
                  }"
                ></div>
              </div>
              <p class="text-slate-500 dark:text-[#7E8DA5] text-[11px] font-medium mt-1.5">
                {{ summaryStats.late > 0 ? "Requires review" : "All staff on schedule" }}
              </p>
            </div>
          </div>

          <!-- Absent Faculty -->
          <div
            class="bg-white dark:bg-[#111C30] border border-slate-200/90 dark:border-[#263449] rounded-2xl p-3.5 sm:p-4 shadow-xs hover:shadow-md hover:border-slate-300 dark:hover:border-slate-700 transition-all duration-300 flex flex-col justify-between overflow-hidden relative group"
          >
            <div>
              <div class="flex items-center justify-between mb-2">
                <div
                  class="w-8 h-8 sm:w-9 sm:h-9 bg-rose-50 dark:bg-rose-950/40 border border-rose-200/60 dark:border-rose-800/40 rounded-xl text-rose-600 dark:text-rose-400 flex items-center justify-center shadow-xs group-hover:scale-105 transition-transform"
                >
                  <UserX class="w-4 h-4 sm:w-4.5 sm:h-4.5" />
                </div>
                <span
                  v-if="summaryStats.total > 0"
                  class="text-[10px] font-bold bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 px-2 py-0.5 rounded-md border border-rose-200/60 dark:border-rose-800/40"
                >
                  {{ absenteeismRate }}% Absent
                </span>
              </div>
              <h3 class="text-xs font-bold text-slate-600 dark:text-[#A6B4C8]">
                Absent Today
              </h3>
              <p class="text-2xl sm:text-3xl font-black text-slate-900 dark:text-[#F1F5F9] mt-0.5 tracking-tight">
                {{ summaryStats.absent }}
              </p>
            </div>
            <div class="mt-2.5">
              <div class="w-full bg-slate-100 dark:bg-[#18243A] h-2 rounded-full overflow-hidden">
                <div
                  class="bg-gradient-to-r from-rose-500 to-red-500 h-full rounded-full transition-all duration-500"
                  :style="{ width: `${absenteeismRate}%` }"
                ></div>
              </div>
              <p class="text-slate-500 dark:text-[#7E8DA5] text-[11px] font-medium mt-1.5">
                Unmarked or absent teachers
              </p>
            </div>
          </div>
        </div>

        <!-- Overall Attendance Session Progress Strip -->
        <div
          v-if="summaryStats.total > 0"
          class="bg-white dark:bg-[#111C30] border border-slate-200/90 dark:border-[#263449] rounded-xl p-3 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-2.5"
        >
          <div class="flex items-center gap-1.5 sm:gap-2 flex-wrap min-w-0">
            <span class="w-2 h-2 rounded-full bg-blue-500 shrink-0"></span>
            <span class="text-xs font-bold text-slate-800 dark:text-slate-100">
              Staff Attendance Rate:
            </span>
            <span class="text-xs font-black text-blue-600 dark:text-blue-400">
              {{ attendanceRate }}%
            </span>
            <span class="text-xs text-slate-400 dark:text-slate-500">
              ({{ summaryStats.present }} of {{ summaryStats.total }} present)
            </span>
          </div>

          <div class="flex items-center gap-2.5 sm:gap-3 w-full sm:w-auto">
            <div class="flex-1 sm:w-48 bg-slate-100 dark:bg-[#1E293B] h-2 rounded-full overflow-hidden flex">
              <div
                class="bg-emerald-500 h-full transition-all duration-500"
                :style="{ width: `${(summaryStats.present / summaryStats.total) * 100}%` }"
                title="Present"
              ></div>
              <div
                class="bg-amber-500 h-full transition-all duration-500"
                :style="{ width: `${(summaryStats.late / summaryStats.total) * 100}%` }"
                title="Late"
              ></div>
              <div
                class="bg-rose-500 h-full transition-all duration-500"
                :style="{ width: `${(summaryStats.absent / summaryStats.total) * 100}%` }"
                title="Absent"
              ></div>
            </div>
            <span class="text-[11px] font-semibold text-slate-500 dark:text-slate-400 shrink-0">
              {{ summaryStats.absent === 0 ? "100% Present" : `${summaryStats.absent} Missing` }}
            </span>
          </div>
        </div>
      </section>

      <!-- Filter & Command Bar -->
      <section
        class="bg-white dark:bg-[#111C30] rounded-xl sm:rounded-2xl border border-slate-200/90 dark:border-[#263449] p-3.5 sm:p-4 shadow-xs space-y-3.5"
      >
        <!-- Section Header -->
        <div class="flex items-center gap-2">
          <div
            class="w-8 h-8 bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 rounded-lg flex items-center justify-center shrink-0"
          >
            <SlidersHorizontal class="w-4 h-4" />
          </div>
          <div>
            <h2 class="text-xs sm:text-sm font-bold text-slate-800 dark:text-slate-100">
              Filter Faculty Attendance
            </h2>
            <p class="text-[11px] text-slate-400 dark:text-slate-500">
              Search staff, select date windows, or filter by status
            </p>
          </div>
        </div>

        <!-- Top Toolbar Row: Search + Range Switcher -->
        <div
          class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3"
        >
          <!-- Search Input -->
          <div class="relative w-full lg:max-w-md">
            <Search
              class="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-[#7E8DA5] h-4 w-4 pointer-events-none"
            />
            <input
              v-model="searchName"
              type="text"
              placeholder="Search faculty by name or ID..."
              class="w-full pl-10 pr-9 py-2.5 min-h-[42px] bg-slate-50 dark:bg-[#18243A] border border-slate-200/90 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-semibold text-slate-900 dark:text-[#F1F5F9] placeholder-slate-400 dark:placeholder-[#7E8DA5] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition"
              @input="debouncedSearch"
            />
            <button
              v-if="searchName"
              @click="clearSearch"
              type="button"
              class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1"
            >
              <X class="w-4 h-4" />
            </button>
          </div>

          <!-- Date Range Selector Segmented Control -->
          <div
            class="grid grid-cols-2 gap-1.5 sm:flex sm:items-center p-1.5 bg-slate-100/90 dark:bg-[#18243A] rounded-xl w-full lg:w-auto"
          >
            <button
              type="button"
              @click="setAttendanceRange('today')"
              :class="[
                'px-3.5 py-2 min-h-[38px] rounded-lg text-xs font-bold transition text-center whitespace-nowrap cursor-pointer',
                attendanceRange === 'today'
                  ? 'bg-white dark:bg-[#111C30] text-blue-600 dark:text-blue-400 shadow-xs border border-slate-200/80 dark:border-[#263449]'
                  : 'text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9]',
              ]"
            >
              Today
            </button>
            <button
              type="button"
              @click="setAttendanceRange('yesterday')"
              :class="[
                'px-3.5 py-2 min-h-[38px] rounded-lg text-xs font-bold transition text-center whitespace-nowrap cursor-pointer',
                attendanceRange === 'yesterday'
                  ? 'bg-white dark:bg-[#111C30] text-blue-600 dark:text-blue-400 shadow-xs border border-slate-200/80 dark:border-[#263449]'
                  : 'text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9]',
              ]"
            >
              Yesterday
            </button>
            <button
              type="button"
              @click="setAttendanceRange('last7days')"
              :class="[
                'px-3.5 py-2 min-h-[38px] rounded-lg text-xs font-bold transition text-center whitespace-nowrap cursor-pointer',
                attendanceRange === 'last7days'
                  ? 'bg-white dark:bg-[#111C30] text-blue-600 dark:text-blue-400 shadow-xs border border-slate-200/80 dark:border-[#263449]'
                  : 'text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9]',
              ]"
            >
              Last 7 Days
            </button>
            <button
              type="button"
              @click="setAttendanceRange('custom')"
              :class="[
                'px-3.5 py-2 min-h-[38px] rounded-lg text-xs font-bold transition text-center whitespace-nowrap cursor-pointer',
                attendanceRange === 'custom'
                  ? 'bg-white dark:bg-[#111C30] text-blue-600 dark:text-blue-400 shadow-xs border border-slate-200/80 dark:border-[#263449]'
                  : 'text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9]',
              ]"
            >
              Custom Range
            </button>
          </div>
        </div>

        <!-- Custom Date Range Sub-Bar -->
        <div
          v-if="attendanceRange === 'custom'"
          class="p-3.5 bg-slate-50 dark:bg-[#18243A]/60 rounded-2xl border border-slate-200/80 dark:border-[#263449] transition-all space-y-3"
        >
          <div class="flex items-center gap-2">
            <CalendarRange class="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <span
              class="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-[#F1F5F9]"
            >
              Custom Period Range:
            </span>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:items-end gap-3">
            <div class="flex-1 space-y-1.5">
              <label
                class="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-[#A6B4C8] block"
              >
                From Date
              </label>
              <div class="relative group">
                <Calendar class="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-[#7E8DA5] pointer-events-none" />
                <input
                  v-model="customDateFrom"
                  type="date"
                  class="w-full pl-10 pr-3 py-2.5 min-h-[42px] bg-white dark:bg-[#111C30] border border-slate-200 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-semibold text-slate-900 dark:text-[#F1F5F9] focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  @change="handleRangeChange"
                />
              </div>
            </div>
            <div class="flex-1 space-y-1.5">
              <label
                class="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-[#A6B4C8] block"
              >
                To Date
              </label>
              <div class="relative group">
                <Calendar class="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-[#7E8DA5] pointer-events-none" />
                <input
                  v-model="customDateTo"
                  type="date"
                  class="w-full pl-10 pr-3 py-2.5 min-h-[42px] bg-white dark:bg-[#111C30] border border-slate-200 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-semibold text-slate-900 dark:text-[#F1F5F9] focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  @change="handleRangeChange"
                />
              </div>
            </div>
            <button
              @click="fetchAttendanceData(1)"
              class="w-full sm:w-auto px-5 py-2.5 min-h-[42px] bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition shadow-xs flex items-center justify-center gap-1.5 cursor-pointer shrink-0"
            >
              <Filter class="w-3.5 h-3.5" />
              <span>Apply Filter</span>
            </button>
          </div>
        </div>

        <!-- Quick Status Filter Pills & Page Size Selector -->
        <div
          class="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 pt-2 border-t border-slate-100 dark:border-[#18243A]"
        >
          <!-- Status Filters -->
          <div class="flex items-center gap-1.5 flex-wrap">
            <span
              class="text-xs font-bold text-slate-500 dark:text-[#7E8DA5] mr-1 flex items-center gap-1"
            >
              <Filter class="w-3.5 h-3.5" /> Filter Status:
            </span>

            <button
              type="button"
              @click="statusFilter = 'all'"
              :class="[
                'px-2.5 py-1 rounded-lg text-xs font-semibold transition',
                statusFilter === 'all'
                  ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 shadow-xs'
                  : 'bg-slate-100 dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] hover:bg-slate-200 dark:hover:bg-[#172235]',
              ]"
            >
              All ({{ summaryStats.total }})
            </button>

            <button
              type="button"
              @click="statusFilter = 'present'"
              :class="[
                'px-2.5 py-1 rounded-lg text-xs font-semibold transition flex items-center gap-1',
                statusFilter === 'present'
                  ? 'bg-emerald-600 text-white shadow-xs'
                  : 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200/50 dark:border-emerald-800/30',
              ]"
            >
              <span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
              Present ({{ summaryStats.present }})
            </button>

            <button
              type="button"
              @click="statusFilter = 'late'"
              :class="[
                'px-2.5 py-1 rounded-lg text-xs font-semibold transition flex items-center gap-1',
                statusFilter === 'late'
                  ? 'bg-amber-600 text-white shadow-xs'
                  : 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200/50 dark:border-amber-800/30',
              ]"
            >
              <span class="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
              Late ({{ summaryStats.late }})
            </button>

            <button
              type="button"
              @click="statusFilter = 'absent'"
              :class="[
                'px-2.5 py-1 rounded-lg text-xs font-semibold transition flex items-center gap-1',
                statusFilter === 'absent'
                  ? 'bg-rose-600 text-white shadow-xs'
                  : 'bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border border-rose-200/50 dark:border-rose-800/30',
              ]"
            >
              <span class="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
              Absent ({{ summaryStats.absent }})
            </button>
          </div>

          <!-- Per-page size selector -->
          <div class="flex items-center justify-between sm:justify-start gap-1.5 w-full sm:w-auto pt-1 sm:pt-0">
            <span class="text-xs text-slate-500 dark:text-[#7E8DA5] font-medium"
              >Rows per page:</span
            >
            <select
              v-model="pageLimit"
              @change="fetchAttendanceData(1)"
              class="px-2.5 py-1 bg-slate-50 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] rounded-lg text-xs font-semibold text-slate-700 dark:text-[#F1F5F9] focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              <option :value="10">10</option>
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
            </select>
          </div>
        </div>
      </section>

      <!-- VIEW MODE 1: Multi-Day Breakdown Table (Last 7 Days or Custom Range) -->
      <section
        v-if="
          (attendanceRange === 'last7days' || attendanceRange === 'custom') &&
          attendanceRecords.length > 0
        "
        class="bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-sm overflow-hidden"
      >
        <!-- Table Top Header -->
        <div
          class="p-4 sm:p-5 border-b border-slate-200/80 dark:border-[#263449] bg-slate-50/50 dark:bg-[#18243A]/40 flex flex-col sm:flex-row sm:items-center justify-between gap-2"
        >
          <div>
            <h3
              class="text-base font-bold text-slate-900 dark:text-[#F1F5F9] flex items-center gap-2"
            >
              <span>Daily Attendance Breakdown</span>
              <span
                class="px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border border-blue-200/50 dark:border-blue-800/40"
              >
                {{ totalDaysInRange }} Days Window
              </span>
            </h3>
            <p class="text-xs text-slate-500 dark:text-[#A6B4C8] mt-0.5">
              Horizontal attendance matrix for {{ attendanceRangeLabel }}
            </p>
          </div>

          <!-- Mini Legend inside header -->
          <div class="flex items-center gap-3 text-xs flex-wrap">
            <span class="inline-flex items-center gap-1 font-medium text-slate-600 dark:text-[#A6B4C8]">
              <span
                class="w-5 h-5 rounded-md bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 font-bold text-[11px] flex items-center justify-center border border-emerald-300 dark:border-emerald-800"
                >P</span
              >
              Present
            </span>
            <span class="inline-flex items-center gap-1 font-medium text-slate-600 dark:text-[#A6B4C8]">
              <span
                class="w-5 h-5 rounded-md bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 font-bold text-[11px] flex items-center justify-center border border-amber-300 dark:border-amber-800"
                >L</span
              >
              Late
            </span>
            <span class="inline-flex items-center gap-1 font-medium text-slate-600 dark:text-[#A6B4C8]">
              <span
                class="w-5 h-5 rounded-md bg-rose-100 dark:bg-rose-950/60 text-rose-800 dark:text-rose-300 font-bold text-[11px] flex items-center justify-center border border-rose-300 dark:border-rose-800"
                >A</span
              >
              Absent
            </span>
            <span class="inline-flex items-center gap-1 font-medium text-slate-600 dark:text-[#A6B4C8]">
              <span
                class="w-5 h-5 rounded-md bg-slate-100 dark:bg-[#18243A] text-slate-500 dark:text-[#7E8DA5] font-bold text-[11px] flex items-center justify-center border border-slate-300 dark:border-[#263449]"
                >-</span
              >
              No Record
            </span>
          </div>
        </div>

        <!-- Scrollable Matrix Table -->
        <div class="overflow-x-auto">
          <table class="w-full text-left border-collapse">
            <thead>
              <tr
                class="bg-slate-50/90 dark:bg-[#0F172A] border-b border-slate-200 dark:border-[#263449]"
              >
                <!-- Teacher Column (Sticky Left) -->
                <th
                  class="sticky left-0 z-10 bg-slate-50 dark:bg-[#0F172A] px-4 sm:px-6 py-3.5 text-xs font-bold text-slate-500 dark:text-[#7E8DA5] uppercase tracking-wider min-w-[200px]"
                >
                  Faculty Member
                </th>
                <!-- Dynamic Date Columns -->
                <th
                  v-for="date in dateRangeArray"
                  :key="date"
                  class="px-3 py-3 text-center text-xs font-bold text-slate-500 dark:text-[#7E8DA5] uppercase tracking-wider min-w-[95px]"
                >
                  <div class="flex flex-col items-center">
                    <span class="text-[10px] text-slate-400 dark:text-[#7E8DA5] font-semibold">
                      {{ formatDateHeader(date).weekday }}
                    </span>
                    <span class="text-xs font-black text-slate-700 dark:text-[#F1F5F9]">
                      {{ formatDateHeader(date).dayMonth }}
                    </span>
                  </div>
                </th>
                <!-- Attendance Ratio Summary Column -->
                <th
                  class="px-4 py-3.5 text-center text-xs font-bold text-slate-500 dark:text-[#7E8DA5] uppercase tracking-wider min-w-[130px]"
                >
                  Attendance Ratio
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 dark:divide-[#18243A]">
              <tr
                v-for="record in displayedGroupedAttendance"
                :key="record._id"
                class="hover:bg-slate-50/70 dark:hover:bg-[#18243A]/50 transition-colors"
              >
                <!-- Teacher (Sticky Column) -->
                <td
                  class="sticky left-0 z-10 bg-white dark:bg-[#111C30] px-4 sm:px-6 py-3.5 whitespace-nowrap shadow-xs"
                >
                  <div class="flex items-center gap-3">
                    <div
                      class="h-9 w-9 rounded-xl flex items-center justify-center bg-gradient-to-br from-blue-500 to-indigo-600 text-white font-bold text-xs shadow-xs overflow-hidden flex-shrink-0"
                    >
                      <img
                        v-if="getTeacherPhoto(record)"
                        :src="getTeacherPhoto(record)"
                        :alt="getTeacherName(record)"
                        class="h-full w-full object-cover"
                      />
                      <span v-else>
                        {{ getInitials(getTeacherName(record)) }}
                      </span>
                    </div>
                    <div class="min-w-0">
                      <div
                        class="text-sm font-bold text-slate-900 dark:text-[#F1F5F9] truncate"
                      >
                        {{ getTeacherName(record) }}
                      </div>
                      <div
                        class="text-xs font-mono text-slate-500 dark:text-[#A6B4C8]"
                      >
                        ID: {{ getTeacherId(record) }}
                      </div>
                    </div>
                  </div>
                </td>

                <!-- Status Cells for Each Date -->
                <td
                  v-for="date in dateRangeArray"
                  :key="`${record._id}-${date}`"
                  class="px-2 py-3 text-center whitespace-nowrap"
                >
                  <div class="flex flex-col items-center justify-center gap-1">
                    <span
                      :class="[
                        'inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-black shadow-2xs transition',
                        getAttendanceStatusForDate(record, date) === 'present'
                          ? 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800/80'
                          : getAttendanceStatusForDate(record, date) === 'late'
                          ? 'bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-800/80'
                          : getAttendanceStatusForDate(record, date) === 'absent'
                          ? 'bg-rose-100 dark:bg-rose-950/60 text-rose-800 dark:text-rose-300 border border-rose-300 dark:border-rose-800/80'
                          : 'bg-slate-100 dark:bg-[#18243A] text-slate-400 dark:text-[#7E8DA5] border border-slate-200 dark:border-[#263449]',
                      ]"
                    >
                      {{
                        getAttendanceStatusForDate(record, date) === "present"
                          ? "P"
                          : getAttendanceStatusForDate(record, date) === "late"
                          ? "L"
                          : getAttendanceStatusForDate(record, date) === "absent"
                          ? "A"
                          : "-"
                      }}
                    </span>
                    <span
                      v-if="getCheckInCheckOutTime(record, date)"
                      class="text-[10px] text-slate-500 dark:text-[#A6B4C8] font-medium"
                    >
                      {{ getCheckInCheckOutTime(record, date) }}
                    </span>
                  </div>
                </td>

                <!-- Presence Ratio & Progress -->
                <td class="px-4 py-3.5 text-center whitespace-nowrap">
                  <div class="inline-flex flex-col items-center gap-1">
                    <div class="flex items-center gap-1.5 font-bold text-xs">
                      <span class="text-blue-600 dark:text-blue-400 font-extrabold text-sm">
                        {{ calculatePresentDaysForTeacher(record) }}
                      </span>
                      <span class="text-slate-400 dark:text-[#7E8DA5]">/</span>
                      <span class="text-slate-600 dark:text-[#A6B4C8]">
                        {{ totalDaysInRange }}
                      </span>
                      <span class="text-[10px] text-slate-400 font-medium ml-0.5">
                        ({{
                          totalDaysInRange > 0
                            ? Math.round(
                                (calculatePresentDaysForTeacher(record) /
                                  totalDaysInRange) *
                                  100
                              )
                            : 0
                        }}%)
                      </span>
                    </div>
                    <!-- Mini progress bar -->
                    <div
                      class="w-18 h-1.5 bg-slate-100 dark:bg-[#18243A] rounded-full overflow-hidden"
                    >
                      <div
                        class="h-full bg-blue-600 dark:bg-blue-500 rounded-full transition-all"
                        :style="{
                          width: `${
                            totalDaysInRange > 0
                              ? Math.min(
                                  100,
                                  Math.round(
                                    (calculatePresentDaysForTeacher(record) /
                                      totalDaysInRange) *
                                      100
                                  )
                                )
                              : 0
                          }%`,
                        }"
                      ></div>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- VIEW MODE 2: Standard Day Records View (Today / Yesterday or fallback) -->
      <section
        v-if="
          !(attendanceRange === 'last7days' || attendanceRange === 'custom') ||
          attendanceRecords.length === 0
        "
        class="bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-sm overflow-hidden"
      >
        <!-- Header bar -->
        <div
          class="p-4 sm:p-5 border-b border-slate-200/80 dark:border-[#263449] bg-slate-50/50 dark:bg-[#18243A]/40 flex flex-col sm:flex-row sm:items-center justify-between gap-2"
        >
          <div>
            <h2
              class="text-base sm:text-lg font-bold text-slate-900 dark:text-[#F1F5F9] flex items-center gap-2"
            >
              <span>Faculty Attendance Roster</span>
              <span
                class="px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border border-blue-200/50 dark:border-blue-800/40"
              >
                {{ attendanceRangeLabel }}
              </span>
            </h2>
            <p class="text-xs text-slate-500 dark:text-[#A6B4C8] mt-0.5">
              Individual staff check-in logs, arrival timestamps, and work hours
            </p>
          </div>

          <div class="text-xs font-semibold text-slate-500 dark:text-[#A6B4C8]">
            Showing {{ displayedAttendanceRecords.length }} of
            {{ attendanceTotal }} total staff
          </div>
        </div>

        <!-- Loading State Indicator -->
        <div
          v-if="isTableLoading"
          class="flex flex-col items-center justify-center py-16"
        >
          <AppLoader
            variant="card"
            size="md"
            message="Loading Attendance Logs..."
            subtext="Syncing faculty punch-in records and biometric logs"
            color="indigo"
          />
        </div>

        <!-- Empty State Container -->
        <div
          v-else-if="displayedAttendanceRecords.length === 0"
          class="text-center py-16 px-4"
        >
          <div
            class="w-16 h-16 rounded-2xl bg-blue-50 dark:bg-[#18243A] text-blue-600 dark:text-blue-400 flex items-center justify-center mx-auto mb-4 border border-blue-100 dark:border-blue-900/30 shadow-xs"
          >
            <Users class="w-8 h-8" />
          </div>
          <h3
            class="text-base sm:text-lg font-bold text-slate-900 dark:text-[#F1F5F9] mb-1"
          >
            No attendance records found
          </h3>
          <p
            class="text-xs sm:text-sm text-slate-500 dark:text-[#A6B4C8] max-w-md mx-auto mb-5"
          >
            {{
              searchName || statusFilter !== "all"
                ? "No faculty matched your active search query or status filter. Try resetting filters."
                : "No attendance data has been logged for this period yet."
            }}
          </p>
          <button
            @click="resetFilters"
            class="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs sm:text-sm font-bold shadow-sm transition"
          >
            <RefreshCw class="w-3.5 h-3.5" />
            <span>Reset Filters & Refresh</span>
          </button>
        </div>

        <!-- Data Content: Responsive Cards (Mobile) + Table (Desktop) -->
        <div v-else>
          <!-- Mobile Cards Layout (< md) -->
          <div class="block md:hidden p-3 sm:p-4 space-y-3">
            <div
              v-for="record in displayedAttendanceRecords"
              :key="record._id"
              class="bg-white dark:bg-[#111C30] rounded-2xl p-4 border border-slate-200/90 dark:border-[#263449] shadow-xs relative overflow-hidden flex flex-col justify-between hover:shadow-md transition-all duration-200"
            >
              <!-- Top Bar: Date / Range & Status Badge -->
              <div class="flex items-center justify-between mb-3">
                <div class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-[#18243A] text-slate-700 dark:text-slate-200 border border-slate-200/80 dark:border-[#263449]">
                  <Calendar class="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                  <span class="text-xs font-bold">
                    {{ attendanceRangeLabel }}
                  </span>
                </div>

                <span
                  :class="[
                    'inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold rounded-full border shadow-2xs capitalize',
                    (record.status || '').toLowerCase() === 'present'
                      ? 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 border-emerald-200/80 dark:border-emerald-800/40'
                      : (record.status || '').toLowerCase() === 'late'
                      ? 'bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300 border-amber-200/80 dark:border-amber-800/40'
                      : 'bg-rose-50 dark:bg-rose-950/50 text-rose-700 dark:text-rose-300 border-rose-200/80 dark:border-rose-800/40'
                  ]"
                >
                  <component
                    :is="(record.status || '').toLowerCase() === 'present' ? CheckCircle2 : (record.status || '').toLowerCase() === 'late' ? Clock : XCircle"
                    class="w-3.5 h-3.5"
                  />
                  <span>{{ record.status || "Unknown" }}</span>
                </span>
              </div>

              <!-- Faculty Profile Section -->
              <div class="flex items-center gap-3 mb-3">
                <div class="relative shrink-0">
                  <div
                    class="w-11 h-11 min-w-[44px] max-w-[44px] min-h-[44px] max-h-[44px] rounded-xl overflow-hidden border border-slate-200/80 dark:border-[#263449] ring-2 ring-slate-100 dark:ring-[#1E293B] flex items-center justify-center bg-slate-100 dark:bg-[#18243A] shadow-2xs"
                  >
                    <img
                      v-if="getTeacherPhoto(record)"
                      :src="getTeacherPhoto(record)"
                      :alt="getTeacherName(record)"
                      class="h-full w-full object-cover block"
                    />
                    <div
                      v-else
                      class="w-full h-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white font-black text-sm uppercase"
                    >
                      {{ getInitials(getTeacherName(record)) }}
                    </div>
                  </div>
                  <!-- Dot -->
                  <div
                    class="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-[#111C30]"
                    :class="[
                      (record.status || '').toLowerCase() === 'present'
                        ? 'bg-emerald-500'
                        : (record.status || '').toLowerCase() === 'late'
                        ? 'bg-amber-500'
                        : 'bg-rose-500'
                    ]"
                  ></div>
                </div>

                <div class="flex-1 min-w-0">
                  <h4 class="text-sm font-black text-slate-900 dark:text-[#F1F5F9] truncate">
                    {{ getTeacherName(record) }}
                  </h4>
                  <div class="flex items-center gap-2 mt-1">
                    <span class="inline-flex items-center gap-1 font-mono text-[11px] font-bold text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-[#18243A] px-2 py-0.5 rounded-md border border-slate-200/80 dark:border-[#263449]">
                      <Hash class="w-3 h-3 text-slate-400" />
                      {{ getTeacherId(record) }}
                    </span>
                    <span
                      v-if="record.userId?.role || record.role"
                      class="text-[11px] font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40 px-2 py-0.5 rounded-md border border-blue-200/60 dark:border-blue-800/40 capitalize truncate max-w-[120px]"
                    >
                      {{ record.userId?.role || record.role }}
                    </span>
                  </div>
                </div>

                <!-- Quick Phone Call Link if available -->
                <a
                  v-if="record.userId?.phone || record.phone"
                  :href="`tel:${record.userId?.phone || record.phone}`"
                  class="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800/40 flex items-center justify-center shrink-0 shadow-xs hover:bg-blue-600 hover:text-white transition-colors"
                  title="Call Faculty"
                >
                  <Phone class="w-4 h-4" />
                </a>
              </div>

              <!-- Time Log Grid (Check-In & Check-Out) -->
              <div class="grid grid-cols-2 gap-2 p-3 bg-slate-50 dark:bg-[#18243A]/60 rounded-xl text-xs mb-3 border border-slate-200/60 dark:border-[#263449]/60">
                <div class="flex items-start gap-2">
                  <div class="w-7 h-7 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200/60 dark:border-emerald-800/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                    <LogIn class="w-3.5 h-3.5" />
                  </div>
                  <div class="min-w-0">
                    <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-[#7E8DA5] block">
                      Check In
                    </span>
                    <p class="font-bold text-slate-900 dark:text-[#F1F5F9] mt-0.5 text-xs">
                      {{ formatTime(record.checkIn) }}
                    </p>
                  </div>
                </div>

                <div class="flex items-start gap-2">
                  <div class="w-7 h-7 rounded-lg bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200/60 dark:border-indigo-800/40 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0">
                    <LogOut class="w-3.5 h-3.5" />
                  </div>
                  <div class="min-w-0">
                    <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-[#7E8DA5] block">
                      Check Out
                    </span>
                    <p class="font-bold text-slate-900 dark:text-[#F1F5F9] mt-0.5 text-xs truncate">
                      {{ record.checkOut ? formatTime(record.checkOut) : "On Premise" }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Duration & Presence Bottom Row -->
              <div class="flex items-center justify-between text-xs pt-2.5 border-t border-slate-100 dark:border-[#263449]/70">
                <span class="text-slate-500 dark:text-[#A6B4C8] flex items-center gap-1.5 font-medium">
                  <Clock3 class="w-3.5 h-3.5 text-blue-500" />
                  <span>Work Duration:</span>
                </span>
                <span class="font-bold text-slate-900 dark:text-[#F1F5F9] bg-slate-100 dark:bg-[#18243A] px-2.5 py-0.5 rounded-lg border border-slate-200/60 dark:border-[#263449]">
                  {{ calculateDuration(record.checkIn, record.checkOut) || "-" }}
                </span>
              </div>

              <!-- Multi-day presence bar if range > 1 day -->
              <div
                v-if="attendanceRange !== 'today' && attendanceRange !== 'yesterday'"
                class="mt-2.5 pt-2.5 border-t border-slate-100 dark:border-[#263449]/70 flex items-center justify-between text-xs"
              >
                <span class="text-slate-500 dark:text-[#A6B4C8] font-medium">
                  Present Days:
                </span>
                <span class="font-bold text-blue-600 dark:text-blue-400">
                  {{ getPresenceCount(record) }} / {{ totalDaysInRange }} days
                </span>
              </div>
            </div>
          </div>

          <!-- Desktop Table View (>= md) -->
          <div class="hidden md:block overflow-x-auto">
            <table class="w-full text-left border-collapse">
              <thead>
                <tr
                  class="bg-slate-50/80 dark:bg-[#0F172A] border-b border-slate-200 dark:border-[#263449]"
                >
                  <th
                    class="px-6 py-3.5 text-xs font-bold text-slate-500 dark:text-[#7E8DA5] uppercase tracking-wider"
                  >
                    Faculty Member
                  </th>
                  <th
                    class="px-6 py-3.5 text-xs font-bold text-slate-500 dark:text-[#7E8DA5] uppercase tracking-wider"
                  >
                    Status
                  </th>
                  <th
                    class="px-6 py-3.5 text-xs font-bold text-slate-500 dark:text-[#7E8DA5] uppercase tracking-wider"
                  >
                    Check In
                  </th>
                  <th
                    class="px-6 py-3.5 text-xs font-bold text-slate-500 dark:text-[#7E8DA5] uppercase tracking-wider"
                  >
                    Check Out
                  </th>
                  <th
                    class="px-6 py-3.5 text-xs font-bold text-slate-500 dark:text-[#7E8DA5] uppercase tracking-wider"
                  >
                    Working Duration
                  </th>
                  <th
                    v-if="
                      attendanceRange !== 'today' &&
                      attendanceRange !== 'yesterday'
                    "
                    class="px-6 py-3.5 text-xs font-bold text-slate-500 dark:text-[#7E8DA5] uppercase tracking-wider"
                  >
                    Present Days
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100 dark:divide-[#18243A]">
                <tr
                  v-for="record in displayedAttendanceRecords"
                  :key="record._id"
                  class="hover:bg-slate-50/80 dark:hover:bg-[#18243A]/50 transition-colors"
                >
                  <!-- Faculty Info -->
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="flex items-center gap-3">
                      <div
                        class="h-9 w-9 rounded-xl flex items-center justify-center bg-gradient-to-br from-blue-500 to-indigo-600 text-white font-bold text-xs shadow-xs overflow-hidden flex-shrink-0"
                      >
                        <img
                          v-if="getTeacherPhoto(record)"
                          :src="getTeacherPhoto(record)"
                          :alt="getTeacherName(record)"
                          class="h-full w-full object-cover"
                        />
                        <span v-else>
                          {{ getInitials(getTeacherName(record)) }}
                        </span>
                      </div>
                      <div>
                        <div
                          class="text-sm font-bold text-slate-900 dark:text-[#F1F5F9]"
                        >
                          {{ getTeacherName(record) }}
                        </div>
                        <div
                          class="text-xs font-mono text-slate-500 dark:text-[#A6B4C8]"
                        >
                          ID: {{ getTeacherId(record) }}
                        </div>
                      </div>
                    </div>
                  </td>

                  <!-- Status Badge -->
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      :class="getStatusBadgeClass(record.status)"
                      class="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full capitalize shadow-2xs"
                    >
                      <span
                        class="w-1.5 h-1.5 rounded-full"
                        :class="[
                          (record.status || '').toLowerCase() === 'present'
                            ? 'bg-emerald-500'
                            : (record.status || '').toLowerCase() === 'late'
                            ? 'bg-amber-500'
                            : 'bg-rose-500',
                        ]"
                      ></span>
                      {{ record.status || "Unknown" }}
                    </span>
                  </td>

                  <!-- Check In -->
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div class="flex items-center gap-1.5 text-slate-700 dark:text-[#F1F5F9]">
                      <Clock
                        v-if="record.checkIn"
                        class="w-3.5 h-3.5 text-blue-500"
                      />
                      <span>{{ formatTime(record.checkIn) }}</span>
                    </div>
                  </td>

                  <!-- Check Out -->
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div class="flex items-center gap-1.5 text-slate-700 dark:text-[#F1F5F9]">
                      <Clock
                        v-if="record.checkOut"
                        class="w-3.5 h-3.5 text-slate-400"
                      />
                      <span v-if="record.checkOut">
                        {{ formatTime(record.checkOut) }}
                      </span>
                      <span
                        v-else
                        class="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-slate-100 dark:bg-[#18243A] text-slate-500 dark:text-[#7E8DA5]"
                      >
                        On Premise / -
                      </span>
                    </div>
                  </td>

                  <!-- Working Duration -->
                  <td class="px-6 py-4 whitespace-nowrap text-sm">
                    <span
                      v-if="calculateDuration(record.checkIn, record.checkOut)"
                      class="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border border-blue-200/50 dark:border-blue-800/40"
                    >
                      <Clock3 class="w-3.5 h-3.5" />
                      {{ calculateDuration(record.checkIn, record.checkOut) }}
                    </span>
                    <span
                      v-else
                      class="text-xs font-medium text-slate-400 dark:text-[#7E8DA5]"
                    >
                      -
                    </span>
                  </td>

                  <!-- Present Days (If multi-day) -->
                  <td
                    v-if="
                      attendanceRange !== 'today' &&
                      attendanceRange !== 'yesterday'
                    "
                    class="px-6 py-4 whitespace-nowrap"
                  >
                    <div class="flex items-center gap-1.5 text-xs font-bold">
                      <span class="text-blue-600 dark:text-blue-400 text-sm font-extrabold">
                        {{ getPresenceCount(record) }}
                      </span>
                      <span class="text-slate-400">/</span>
                      <span class="text-slate-600 dark:text-[#A6B4C8]">
                        {{ totalDaysInRange }}
                      </span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Modern Pagination Bar -->
        <div
          v-if="displayedAttendanceRecords.length > 0"
          class="bg-slate-50/70 dark:bg-[#0F172A] px-4 sm:px-6 py-3.5 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-slate-200 dark:border-[#263449]"
        >
          <div class="text-xs sm:text-sm font-medium text-slate-600 dark:text-[#A6B4C8]">
            Showing
            <span class="font-bold text-slate-900 dark:text-[#F1F5F9]">{{
              getStartRecord()
            }}</span>
            to
            <span class="font-bold text-slate-900 dark:text-[#F1F5F9]">{{
              getEndRecord()
            }}</span>
            of
            <span class="font-bold text-slate-900 dark:text-[#F1F5F9]">{{
              attendanceTotal
            }}</span>
            faculty members
          </div>

          <div class="flex items-center gap-1.5">
            <button
              @click="previousPage"
              :disabled="currentPage <= 1"
              type="button"
              class="flex items-center gap-1 px-3 py-1.5 bg-white dark:bg-[#111C30] border border-slate-200 dark:border-[#263449] rounded-xl text-xs font-bold text-slate-700 dark:text-[#F1F5F9] hover:bg-slate-50 dark:hover:bg-[#18243A] disabled:opacity-40 disabled:cursor-not-allowed transition shadow-2xs"
            >
              <ChevronLeft class="h-4 w-4" />
              <span>Prev</span>
            </button>

            <!-- Page Number Pills -->
            <div class="hidden sm:flex items-center gap-1">
              <button
                v-for="page in visiblePages"
                :key="page"
                @click="goToPage(page)"
                type="button"
                :class="[
                  'w-8 h-8 rounded-xl text-xs font-bold transition flex items-center justify-center',
                  page === currentPage
                    ? 'bg-blue-600 text-white shadow-xs'
                    : 'bg-white dark:bg-[#111C30] border border-slate-200 dark:border-[#263449] text-slate-700 dark:text-[#F1F5F9] hover:bg-slate-50 dark:hover:bg-[#18243A]',
                ]"
              >
                {{ page }}
              </button>
            </div>

            <button
              @click="nextPage"
              :disabled="currentPage >= totalPages"
              type="button"
              class="flex items-center gap-1 px-3 py-1.5 bg-white dark:bg-[#111C30] border border-slate-200 dark:border-[#263449] rounded-xl text-xs font-bold text-slate-700 dark:text-[#F1F5F9] hover:bg-slate-50 dark:hover:bg-[#18243A] disabled:opacity-40 disabled:cursor-not-allowed transition shadow-2xs"
            >
              <span>Next</span>
              <ChevronRight class="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, onUnmounted } from "vue";
  import { useStore } from "vuex";
  import {
    Bell,
    UserCheck,
    UserX,
    Clock,
    Clock3,
    Users,
    Search,
    ChevronLeft,
    ChevronRight,
    RefreshCw,
    AlertCircle,
    CheckCircle,
    CheckCircle2,
    XCircle,
    Info,
    Calendar,
    CalendarRange,
    X,
    Filter,
    SlidersHorizontal,
    LogIn,
    LogOut,
    Hash,
    Phone,
  } from "lucide-vue-next";
  import Banner from "@/components/Banner/TeacherBanner.vue";

  const store = useStore();

  // Element Refs for Outside Click Handling
  const notificationButtonRef = ref(null);
  const notificationDropdownRef = ref(null);

  // Auto Refresh Intervals
  const attendanceInterval = ref(null);
  const notificationInterval = ref(null);

  // Reactive State
  const showNotifications = ref(false);
  const searchName = ref("");
  const statusFilter = ref("all");
  const selectedDate = ref(new Date().toISOString().split("T")[0]);
  const pageLimit = ref(10);
  const currentPage = ref(1);
  const isTableLoading = ref(false);
  const isLoadingNotifications = ref(false);

  // Attendance Range State Management
  const attendanceRange = ref("today");
  const customDateFrom = ref("");
  const customDateTo = ref("");

  // Store Getters
  const attendanceRecords = computed(() => {
    return store.state.attendanceRecords || [];
  });

  const attendanceTotal = computed(() => {
    return store.state.attendanceTotal || 0;
  });

  const notifications = computed(() => {
    const rawNotifications = store.state.notifications || [];
    return rawNotifications.map((notification, index) => {
      if (typeof notification === "string") {
        return {
          id: `notification_${index}`,
          message: notification,
          type: "info",
          severity: "info",
          createdAt: new Date().toISOString(),
          title: "Attendance Alert",
        };
      }
      return notification;
    });
  });

  // Computed Summary Stats
  const summaryStats = computed(() => {
    const records = attendanceRecords.value;
    const total = records.length;
    const present = records.filter(
      (r) => (r.status || "").toLowerCase() === "present"
    ).length;
    const late = records.filter(
      (r) => (r.status || "").toLowerCase() === "late"
    ).length;
    const absent = records.filter(
      (r) => (r.status || "").toLowerCase() === "absent"
    ).length;

    return { total, present, late, absent };
  });

  const attendanceRate = computed(() => {
    if (!summaryStats.value.total) return 0;
    return Math.round(
      (summaryStats.value.present / summaryStats.value.total) * 100
    );
  });

  const absenteeismRate = computed(() => {
    if (!summaryStats.value.total) return 0;
    return Math.round(
      (summaryStats.value.absent / summaryStats.value.total) * 100
    );
  });

  // Filtered Single-Day Records based on status filter
  const displayedAttendanceRecords = computed(() => {
    const records = attendanceRecords.value;
    if (statusFilter.value === "all") return records;
    return records.filter(
      (r) => (r.status || "").toLowerCase() === statusFilter.value
    );
  });

  // Human-readable Range Label
  const attendanceRangeLabel = computed(() => {
    switch (attendanceRange.value) {
      case "yesterday": {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        return yesterday.toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      }
      case "last7days": {
        const from = new Date();
        from.setDate(from.getDate() - 6);
        const to = new Date();
        return `${from.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        })} - ${to.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })}`;
      }
      case "custom":
        if (customDateFrom.value && customDateTo.value) {
          return `${customDateFrom.value} to ${customDateTo.value}`;
        }
        return "Custom Range";
      default:
        return new Date().toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
          year: "numeric",
        });
    }
  });

  // Days in range calculation
  const totalDaysInRange = computed(() => {
    if (attendanceRange.value === "yesterday") return 1;
    if (attendanceRange.value === "last7days") return 7;
    if (
      attendanceRange.value === "custom" &&
      customDateFrom.value &&
      customDateTo.value
    ) {
      const from = new Date(customDateFrom.value);
      const to = new Date(customDateTo.value);
      const diffTime = Math.abs(to - from);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return diffDays;
    }
    return 1;
  });

  // Multi-day Date Array
  const dateRangeArray = computed(() => {
    const dates = [];
    let startDate, endDate;

    if (attendanceRange.value === "last7days") {
      endDate = new Date();
      startDate = new Date();
      startDate.setDate(startDate.getDate() - 6);
    } else if (attendanceRange.value === "custom") {
      if (!customDateFrom.value || !customDateTo.value) return dates;
      startDate = new Date(customDateFrom.value);
      endDate = new Date(customDateTo.value);
    } else {
      return dates;
    }

    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      dates.push(currentDate.toISOString().split("T")[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  });

  // Grouped attendance for multi-day table
  const groupedAttendanceByTeacher = computed(() => {
    const grouped = {};

    attendanceRecords.value.forEach((record) => {
      const teacherId = record.userId?._id || record._id;
      const teacherName = getTeacherName(record);

      if (!grouped[teacherId]) {
        grouped[teacherId] = {
          _id: teacherId,
          name: teacherName,
          userId: record.userId,
          photo: getTeacherPhoto(record),
          attendanceID: getTeacherId(record),
          dailyAttendance: [],
        };
      }

      grouped[teacherId].dailyAttendance.push({
        date: record.date,
        status: record.status,
        checkIn: record.checkIn,
        checkOut: record.checkOut,
      });
    });

    return Object.values(grouped);
  });

  const displayedGroupedAttendance = computed(() => {
    const groups = groupedAttendanceByTeacher.value;
    if (statusFilter.value === "all") return groups;
    return groups.filter((g) => {
      return g.dailyAttendance.some(
        (day) => (day.status || "").toLowerCase() === statusFilter.value
      );
    });
  });

  // Pagination Logic
  const totalPages = computed(() =>
    Math.max(1, Math.ceil(attendanceTotal.value / pageLimit.value))
  );

  const visiblePages = computed(() => {
    const pages = [];
    const start = Math.max(1, currentPage.value - 2);
    const end = Math.min(totalPages.value, currentPage.value + 2);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  });

  // Data Extraction Helpers
  const getTeacherName = (record) => {
    return record.userId?.name || record.name || "Unknown Faculty";
  };

  const getTeacherPhoto = (record) => {
    return record.userId?.photo || record.photo || null;
  };

  const getTeacherId = (record) => {
    return (
      record.userId?.attendanceID ||
      record.attendanceID ||
      record.userId?._id?.substring(0, 6) ||
      "N/A"
    );
  };

  const getPresenceCount = (record) => {
    return record.presentDays || record.presentCount || 0;
  };

  const formatDateHeader = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return { weekday: "", dayMonth: dateString };
      }
      return {
        weekday: date.toLocaleDateString("en-US", { weekday: "short" }),
        dayMonth: date.toLocaleDateString("en-US", {
          day: "numeric",
          month: "short",
        }),
      };
    } catch {
      return { weekday: "", dayMonth: dateString };
    }
  };

  const getAttendanceStatusForDate = (record, dateString) => {
    if (!record.dailyAttendance || !Array.isArray(record.dailyAttendance)) {
      return "no-data";
    }

    const dailyRecord = record.dailyAttendance.find(
      (day) => day.date === dateString
    );

    if (!dailyRecord) return "no-data";

    const status = dailyRecord.status?.toLowerCase();
    if (status === "present" || status === "p") return "present";
    if (status === "late" || status === "l") return "late";
    if (status === "absent" || status === "a") return "absent";

    return "no-data";
  };

  const calculatePresentDaysForTeacher = (record) => {
    if (!record.dailyAttendance || !Array.isArray(record.dailyAttendance)) {
      return 0;
    }

    return record.dailyAttendance.filter((day) => {
      const status = day.status?.toLowerCase();
      return status === "present" || status === "p";
    }).length;
  };

  const getCheckInCheckOutTime = (record, dateString) => {
    if (!record.dailyAttendance || !Array.isArray(record.dailyAttendance)) {
      return null;
    }

    const dailyRecord = record.dailyAttendance.find(
      (day) => day.date === dateString
    );

    if (!dailyRecord || !dailyRecord.checkIn) {
      return null;
    }

    const checkInTime = formatTime(dailyRecord.checkIn);
    if (dailyRecord.checkOut) {
      const checkOutTime = formatTime(dailyRecord.checkOut);
      return `${checkInTime} - ${checkOutTime}`;
    }

    return checkInTime;
  };

  // Notification Helpers
  const getNotificationMessage = (notification) => {
    return notification.message || notification;
  };

  const getNotificationTitle = (notification) => {
    return notification.title || "Attendance Notice";
  };

  const getNotificationSeverity = (notification) => {
    return notification.severity || "info";
  };

  const getNotificationIcon = (notification) => {
    const severity = getNotificationSeverity(notification);
    switch (severity) {
      case "error":
        return AlertCircle;
      case "warning":
        return Clock;
      case "info":
        return Info;
      default:
        return CheckCircle;
    }
  };

  const getNotificationIconClass = (notification) => {
    const severity = getNotificationSeverity(notification);
    switch (severity) {
      case "error":
        return "bg-rose-100 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400";
      case "warning":
        return "bg-amber-100 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400";
      case "info":
        return "bg-blue-100 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400";
      default:
        return "bg-emerald-100 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400";
    }
  };

  const getNotificationSeverityClass = (notification) => {
    const severity = getNotificationSeverity(notification);
    switch (severity) {
      case "error":
        return "bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800/40";
      case "warning":
        return "bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800/40";
      case "info":
        return "bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/40";
      default:
        return "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/40";
    }
  };

  const formatNotificationTime = (notification) => {
    const createdAt = notification.createdAt || new Date().toISOString();
    try {
      const date = new Date(createdAt);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;

      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "Recently";
    }
  };

  // State Updates & Fetch Handlers
  const setAttendanceRange = (range) => {
    attendanceRange.value = range;
    if (range === "yesterday") {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      selectedDate.value = yesterday.toISOString().split("T")[0];
    } else if (range === "last7days") {
      const from = new Date();
      from.setDate(from.getDate() - 6);
      customDateFrom.value = from.toISOString().split("T")[0];
      customDateTo.value = new Date().toISOString().split("T")[0];
    } else if (range === "custom") {
      // Keep existing custom dates
    } else {
      selectedDate.value = new Date().toISOString().split("T")[0];
    }
    currentPage.value = 1;
    fetchAttendanceData();
  };

  const handleRangeChange = () => {
    currentPage.value = 1;
    if (attendanceRange.value === "yesterday") {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      selectedDate.value = yesterday.toISOString().split("T")[0];
    }
    if (attendanceRange.value === "today") {
      selectedDate.value = new Date().toISOString().split("T")[0];
    }
    fetchAttendanceData();
  };

  const fetchAttendanceData = async (
    page = currentPage.value,
    silent = false
  ) => {
    if (!silent) {
      isTableLoading.value = true;
    }

    try {
      currentPage.value = Number(page);
      const limit = Number(pageLimit.value);

      const payload = {
        page: currentPage.value,
        limit: limit,
        name: searchName.value.trim(),
        range: attendanceRange.value,
      };

      if (attendanceRange.value === "custom") {
        payload.dateFrom = customDateFrom.value;
        payload.dateTo = customDateTo.value;
      } else if (attendanceRange.value === "yesterday") {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        payload.date = yesterday.toISOString().split("T")[0];
      } else if (attendanceRange.value === "last7days") {
        payload.dateFrom = customDateFrom.value;
        payload.dateTo = customDateTo.value;
      } else {
        payload.date = selectedDate.value;
      }

      await store.dispatch("fetchAttendanceForToday", payload);
    } catch (error) {
      console.error("❌ Error fetching attendance data:", error);
    } finally {
      if (!silent) {
        isTableLoading.value = false;
      }
    }
  };

  const fetchNotifications = async (silent = false) => {
    if (!silent) {
      isLoadingNotifications.value = true;
    }

    try {
      await store.dispatch("fetchAttendanceNotifications");
    } catch (error) {
      console.error("❌ Failed to fetch notifications:", error);
    } finally {
      if (!silent) {
        isLoadingNotifications.value = false;
      }
    }
  };

  const refreshNotifications = async () => {
    await fetchNotifications();
  };

  // Debounced Search
  let searchTimeout;
  const debouncedSearch = () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      currentPage.value = 1;
      fetchAttendanceData();
    }, 400);
  };

  const clearSearch = () => {
    searchName.value = "";
    currentPage.value = 1;
    fetchAttendanceData();
  };

  const resetFilters = () => {
    searchName.value = "";
    statusFilter.value = "all";
    attendanceRange.value = "today";
    currentPage.value = 1;
    fetchAttendanceData();
  };

  // Pagination Controls
  const previousPage = () => {
    if (currentPage.value > 1) {
      currentPage.value--;
      fetchAttendanceData();
    }
  };

  const nextPage = () => {
    if (currentPage.value < totalPages.value) {
      currentPage.value++;
      fetchAttendanceData();
    }
  };

  const goToPage = (page) => {
    currentPage.value = page;
    fetchAttendanceData();
  };

  // Status Styling Badge
  const getStatusBadgeClass = (status) => {
    const statusLower = (status || "").toLowerCase();
    switch (statusLower) {
      case "present":
        return "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200/80 dark:border-emerald-800/40";
      case "late":
        return "bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200/80 dark:border-amber-800/40";
      case "absent":
        return "bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border border-rose-200/80 dark:border-rose-800/40";
      default:
        return "bg-slate-100 dark:bg-[#18243A] text-slate-700 dark:text-[#A6B4C8] border border-slate-200 dark:border-[#263449]";
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "-";
    const date = new Date(timeString);
    if (isNaN(date.getTime())) return "-";

    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const calculateDuration = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return null;

    const start = new Date(checkIn);
    const end = new Date(checkOut);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;

    const diffMs = end - start;
    if (diffMs <= 0) return null;

    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    return `${diffHours}h ${diffMinutes}m`;
  };

  const getStartRecord = () => {
    const start = (currentPage.value - 1) * Number(pageLimit.value) + 1;
    return isNaN(start) ? 1 : start;
  };

  const getEndRecord = () => {
    const end = Math.min(
      currentPage.value * Number(pageLimit.value),
      attendanceTotal.value
    );
    return isNaN(end) ? attendanceTotal.value : end;
  };

  const getInitials = (name) => {
    if (!name) return "--";
    const words = name.trim().split(" ").filter(Boolean);
    if (words.length === 1) {
      return words[0].slice(0, 2).toUpperCase();
    }
    const firstLetter = words[0][0];
    const lastLetter = words[words.length - 1][0];
    return `${firstLetter}${lastLetter}`.toUpperCase();
  };

  // Background Auto-Refresh
  const startAutoRefresh = () => {
    attendanceInterval.value = setInterval(() => {
      if (!isTableLoading.value) {
        fetchAttendanceData(currentPage.value, true);
      }
    }, 10000);

    notificationInterval.value = setInterval(() => {
      if (!isLoadingNotifications.value) {
        fetchNotifications(true);
      }
    }, 15000);
  };

  // Outside Click Listener for Notifications
  const handleDocumentClick = (event) => {
    if (!showNotifications.value) return;

    const dropdown = notificationDropdownRef.value;
    const button = notificationButtonRef.value;

    if (
      dropdown &&
      !dropdown.contains(event.target) &&
      button &&
      !button.contains(event.target)
    ) {
      showNotifications.value = false;
    }
  };

  // Lifecycle
  onMounted(() => {
    // Setup initial custom date range
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 6);
    customDateFrom.value = from.toISOString().split("T")[0];
    customDateTo.value = to.toISOString().split("T")[0];

    fetchAttendanceData();
    fetchNotifications();
    startAutoRefresh();

    document.addEventListener("click", handleDocumentClick);
  });

  onUnmounted(() => {
    if (attendanceInterval.value) {
      clearInterval(attendanceInterval.value);
    }
    if (notificationInterval.value) {
      clearInterval(notificationInterval.value);
    }
    document.removeEventListener("click", handleDocumentClick);
  });
</script>

<style scoped>
  /* Custom scrollbar for notifications and matrix table */
  .overflow-y-auto::-webkit-scrollbar,
  .overflow-x-auto::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  .overflow-y-auto::-webkit-scrollbar-track,
  .overflow-x-auto::-webkit-scrollbar-track {
    background: transparent;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb,
  .overflow-x-auto::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 9999px;
  }

  .dark .overflow-y-auto::-webkit-scrollbar-thumb,
  .dark .overflow-x-auto::-webkit-scrollbar-thumb {
    background: #263449;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb:hover,
  .overflow-x-auto::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
</style>
